import type { AddonId } from './plans';
import type { EstadoFacturacion, TaxProfile } from './tax';
import type { Unit, UnitSystem } from './units';
import type { PlatformReparto } from './platformSplit';

/**
 * Modelo de dominio, calcado de las plantillas v2.0.0 de Alex Cortijo.
 * Ver ANALISIS-PLANTILLAS.md para el mapeo campo a campo.
 *
 * Recordatorio: todo campo `...Pct` va de 0 a 100.
 */

/* ------------------------------------------------------------------ */
/* Tenancy                                                             */
/* ------------------------------------------------------------------ */

export type PlanId = 'trial' | 'basic' | 'pro' | 'founder';
export type SubscriptionStatus = 'trialing' | 'active' | 'past_due' | 'canceled';

export interface Tenant {
  tenantId: string;
  name: string;
  /**
   * Pais del negocio en ISO 3166-1 alfa-2 (cualquiera de `PAISES`). Los
   * tenants dados de alta antes del 7 sep 2026 pueden llevar «OTHER»: la
   * pantalla de Ajustes les pide elegir uno.
   */
  country: string;
  currency: string; // ISO 4217
  plan: PlanId;
  subscriptionStatus: SubscriptionStatus;
  stripeCustomerId?: string;
  /**
   * Id del contacto en Holded. Se guarda al emitir la primera factura para no
   * tener que buscarlo por NIF en cada consulta, y es lo que acota que un
   * restaurante solo pueda ver SUS facturas.
   */
  holdedContactId?: string;
  stripeSubscriptionId?: string;
  /**
   * `created` (epoch, segundos) del ultimo evento de suscripcion de Stripe
   * aplicado. Los webhooks pueden llegar desordenados: un evento mas viejo
   * que este se ignora para no pisar un estado mas nuevo.
   */
  /**
   * Epoch del ultimo evento de SUSCRIPCION aplicado (created/updated/deleted
   * e invoice.payment_failed). Sirve para no pisar el plan con un evento que
   * llega tarde y desordenado.
   */
  stripeLastEventAt?: number;
  /**
   * Epoch del ultimo `checkout.session.completed` aplicado.
   *
   * Va aparte del anterior a proposito. Stripe crea la suscripcion ANTES de
   * cerrar el checkout, asi que `customer.subscription.created` puede llevar
   * un `created` menor que el del checkout. Con un solo reloj compartido, el
   * checkout lo adelantaba y el evento que fija el plan se descartaba por
   * viejo: el cliente pagaba y se quedaba en "Prueba gratuita".
   */
  stripeLastCheckoutEventAt?: number;
  trialEndsAt?: string;
  /**
   * true = se dio de alta con una invitación Founder y AÚN no ha pagado.
   * Los Founder no tienen prueba gratuita: mientras este flag esté puesto,
   * la app entera queda bloqueada en la página de pago. Lo quita el webhook
   * de Stripe cuando la suscripción se activa.
   */
  founderPending?: boolean;
  /**
   * Numero de WhatsApp del propietario. Se pide en el alta de los Founder
   * (Alex, 4 sep 2026) como canal directo para acompanyarles, pedir feedback
   * y ayudarles si algo falla en esta primera etapa. SOLO soporte y feedback
   * sobre Alexia, nunca marketing: es lo que se le promete al pedirlo.
   * Cualquier restaurante puede ponerlo o cambiarlo en Ajustes.
   */
  whatsapp?: string;
  /**
   * Cuando el propietario marco en el alta la casilla «quiero recibir
   * novedades y consejos por correo» (ISO). Ausente = no la marco. Es lo
   * que se manda a beehiiv como campo `newsletter` para segmentar las
   * campanas: sin esta fecha no hay permiso para marketing.
   */
  newsletterConsentAt?: string;
  /** Modulos de pago contratados (escaner de facturas...). Ver plans.ts. */
  addons?: AddonId[];
  /**
   * Asientos extra contratados por encima de los que incluye el plan
   * (5 €/mes cada uno). Sale de la cantidad de la linea de Stripe, igual que
   * `plan` y `addons`: los webhooks son la unica fuente de verdad.
   */
  extraSeats?: number;
  /**
   * Centros contratados por encima del único centro incluido en todos los
   * planes (9 €/mes cada uno). Lo fija exclusivamente el webhook de Stripe.
   */
  extraLocations?: number;
  /**
   * Locales de cortesia (7 sep 2026): se suman al limite sin cobrarlos.
   * Los pone el panel de administracion y Stripe NO los toca, asi que
   * sobreviven a cualquier webhook. Para dejar probar a un cliente un
   * segundo restaurante sin pasar por caja.
   */
  bonusLocations?: number;
  /**
   * Datos fiscales para poder emitirle factura. Quien presta el servicio es
   * Polar Consulting Group 33 SL: Stripe solo cobra, la factura la emitimos
   * nosotros. Sin esto no hay factura valida. Ver `tax.ts`.
   */
  tax?: TaxProfile;
  /**
   * Cuestionario de configuracion inicial (Alex, 5 sep 2026): al darse de
   * alta, cinco pasos que dejan Ajustes relleno antes de ver la app. `step`
   * es el paso por el que va (para retomarlo si cierra a mitad) y
   * `completedAt` marca que lo termino o lo salto. AUSENTE = restaurante
   * anterior al cuestionario: se da por configurado y no se le molesta.
   */
  setup?: TenantSetup;
  /**
   * Cuenta INTERNA (las de prueba de Pol y Alex): se ve en Administracion,
   * pero no cuenta en ningun numero del negocio (MRR, clientes, coste de IA,
   * ingresos de Stripe). Se marca desde «Gestionar».
   */
  internal?: boolean;
  settings: TenantSettings;
  createdAt: string;
}

/** Ultimo paso del cuestionario inicial (1 alta, 2 fiscal, 3 ventas, 4 TPV, 5 objetivos). Lo usan la API (validacion) y la web (asistente). */
export const SETUP_LAST_STEP = 5;

export interface TenantSetup {
  /** Paso pendiente, de 1 (alta) a SETUP_LAST_STEP. */
  step: number;
  /** ISO. Puesto = el cuestionario ya no se ensenya. */
  completedAt?: string;
}

/** Equivale a los parametros globales del Dashboard y de la hoja Precios. */
export interface TenantSettings {
  /** IVA por defecto de las ventas */
  vatPct: number;
  /** Comision del datafono / TPV, se descuenta del margen en ventas en local */
  bankCommissionPct: number;
  /** Plataformas de delivery con su comision */
  platforms: DeliveryPlatform[];
  /*
   * Aqui vivio `revenueBreakdown` ('metodo_pago' | 'tipo_producto'): el
   * usuario elegia en Ajustes como agrupar los ingresos. Se quito el 4 de
   * septiembre de 2026 a peticion de Alex: los ingresos se clasifican SIEMPRE
   * por metodo de pago (y plataforma), que es lo unico que se puede alimentar
   * de forma consistente desde cualquier TPV. La otra clasificacion no se
   * podia importar bien ni se reflejaba en la cuenta de resultados. Un
   * ajuste guardado con la clave antigua se ignora sin mas.
   */
  /**
   * Grupos de ingredientes propios del restaurante, ademas de los 12 de
   * serie. Peticion de Alex: los de serie vienen dados, y quien quiera mas
   * los anyade en Ajustes.
   */
  customIngredientGroups?: string[];
  /**
   * Subgrupos de gasto del restaurante, colgando de los 3 grupos de Alex.
   * Clave: 'materia_prima' | 'personal' | 'generales'. Ejemplos: Carnes,
   * Pescados, Cocina, Sala. Solo presentacion y analisis: el plan de
   * cuentas (las 22 categorias) no se toca.
   */
  expenseSubgroups?: Partial<Record<'materia_prima' | 'personal' | 'generales', string[]>>;
  /**
   * Sistema de medidas del restaurante: decide que unidades se ofrecen
   * primero al dar de alta un ingrediente y cual se propone por defecto.
   * NO convierte nada de lo ya guardado.
   */
  unitSystem?: UnitSystem;
  /**
   * Orden manual de la carta (grupos y platos), comun a todos los canales.
   * Vacio = el orden por defecto. Ver `MenuOrder` en costing.ts.
   */
  menuOrder?: { groups?: string[]; items?: string[] };
  /**
   * Grupos de ingredientes de serie que este restaurante no usa. No se
   * borran de verdad —los datos que ya los usen se siguen viendo—, dejan de
   * ofrecerse en los desplegables. Un asador no quiere elegir entre doce
   * grupos cuando usa cuatro.
   */
  hiddenIngredientGroups?: string[];
  /**
   * Categorias de gasto que no se ofrecen. OJO: las 22 categorias son el
   * plan de cuentas del que cuelga la cuenta de resultados (que es materia
   * prima, que es personal, que va bajo el EBITDA), asi que NO se pueden
   * eliminar de verdad sin romper los informes. Ocultarlas hace lo que el
   * usuario espera —no verlas— sin tocar el motor ni el historico.
   */
  hiddenExpenseCategories?: string[];
  /**
   * Avisos de la carta que el usuario ya ha mirado y ha decidido dejar como
   * estan ("Entendido"). No es un silencio para siempre: se guarda COMO estaba
   * la cosa al reconocerlo, y si empeora de forma relevante el aviso vuelve.
   * Ver `cartaAlerts` en costing.ts.
   */
  alertAcks?: AlertAck[];
  /** Reparto de los gastos comunes (`SHARED_LOCATION_ID`) entre locales. */
  sharedCostAllocation?: SharedCostAllocation;
  alerts: AlertSettings;
}

/** Un aviso de la carta reconocido por el usuario. */
export interface AlertAck {
  /**
   * Clave estable del aviso: tipo + plato. Estable a proposito, para que el
   * mismo problema del mismo plato siga reconocido aunque cambie el numero
   * que sale en el texto.
   */
  key: string;
  /**
   * La metrica en el momento de reconocerlo: el margen bruto en % o la
   * desviacion de coste en %. Es lo que permite volver a avisar solo cuando
   * la situacion empeora de verdad.
   */
  value?: number;
  /**
   * Texto del aviso reconocido. Para los platos que no se pueden costear no
   * hay numero que comparar: si el motivo cambia, es otro problema y vuelve.
   */
  detail?: string;
  /** Cuando se reconocio (ISO). Solo informativo. */
  at: string;
}

export interface DeliveryPlatform {
  platformId: string;
  name: string; // Uber Eats, Glovo, Just Eat...
  commissionPct: number;
  active: boolean;
}

export interface AlertSettings {
  /** Salta en rojo si el coste de un plato sube mas de este % */
  costIncreasePct: number; // 5
  /** Salta en verde si el coste baja mas de este % */
  costDecreasePct: number; // 10
  /** Salta en rojo si el margen bruto cae por debajo de este % */
  minGrossMarginPct: number; // 65
}

export const DEFAULT_SETTINGS: TenantSettings = {
  vatPct: 10,
  bankCommissionPct: 2.1,
  platforms: [],
  alerts: { costIncreasePct: 5, costDecreasePct: 10, minGrossMarginPct: 65 },
};

/**
 * Roles dentro de un tenant (decision de Pol y Alex, 21 ago 2026):
 * - `owner` (propietario): todo — datos, equipo, plan, facturacion.
 * - `admin`: reservado; hoy equivale a owner en las guardas de rutas. No se
 *   ofrece en la UI de invitaciones.
 * - `staff` (encargado): opera ventas, gastos, recetas y escaner, pero NO ve
 *   la P&L (ni Analisis ni cuenta de resultados) ni toca plan/equipo.
 * - `viewer` (lectura): solo consulta. Pensado para la gestoria; ve la P&L
 *   pero no puede escribir nada.
 * - `kitchen` (cocina, 7 sep 2026): solo la seccion Costeo — ingredientes,
 *   inventario, proveedores, recetas y precios de la carta. Ni ventas, ni
 *   gastos, ni resultados, ni asistente. Pedido por un cliente que queria dar
 *   acceso a su jefe de cocina sin ensenyarle los datos generales.
 */
export type Role = 'owner' | 'admin' | 'staff' | 'viewer' | 'kitchen';

export type MembershipStatus = 'active' | 'disabled';

export interface Membership {
  tenantId: string;
  userId: string;
  email: string;
  name?: string;
  role: Role;
  /**
   * Las membresias anteriores a multi-usuario no llevan `status`: ausente
   * equivale a `active`. Dar de baja es pasar a `disabled` (reversible), nunca
   * borrar — el historial de quien metio cada dato no se pierde.
   */
  status?: MembershipStatus;
  /** userId del propietario que envio la invitacion. */
  invitedBy?: string;
  /**
   * `username` inmutable de Cognito con el que se direccionan los cambios de
   * rol y las bajas. NUNCA se usa el email (atributo mutable) para eso. Las
   * membresias anteriores a multi-usuario no lo tienen: cae al `userId` (sub),
   * que Cognito tambien acepta como identificador.
   */
  cognitoUsername?: string;
  /**
   * ISO de cuando este usuario vio (o salto) el tour guiado de la app. Va
   * por persona, no por restaurante: un encargado invitado despues tambien
   * lo recibe, adaptado a lo que ve su rol.
   */
  tourSeenAt?: string;
  createdAt: string;
  updatedAt?: string;
}

/**
 * Centro (local) de un restaurante — sembrado multi-centro (21 ago 2026).
 *
 * El centro es una dimension DENTRO del tenant, no un tenant por local. Todo
 * tenant nace con un centro `principal`, y los registros operativos llevan un
 * `locationId` opcional con convencion fija: **ausente = centro principal**.
 * Gracias a eso, construir la UI multi-centro el dia de manyana no exige
 * migrar ningun dato historico, y la P&L consolidada sigue siendo la suma de
 * todo el tenant.
 *
 * El catalogo (recetas, carta) es del tenant; cuando un grupo necesite
 * cartas o precios distintos por local (decision de Alex: pueden cambiar),
 * se anyadira un ambito por centro y un `locationPrices` primo de
 * `channelPrices` — sin tocar este modelo.
 */
export interface Location {
  tenantId: string;
  locationId: string;
  name: string;
  active: boolean;
}

/** Id fijo del centro con el que nace todo tenant. */
export const DEFAULT_LOCATION_ID = 'principal';

/**
 * Local ficticio «Grupo» (7 sep 2026): un gasto con este `locationId` es
 * COMUN a todos los locales (gestoria, oficina, gerente) y la cuenta de
 * resultados de cada local se lleva su parte segun `sharedCostAllocation`.
 * En el consolidado entra entero. No existe como fila LOCATION#.
 */
export const SHARED_LOCATION_ID = 'grupo';

/** `locationId` ausente = centro principal. */
export function effectiveLocationId(value?: string): string {
  return value?.trim() || DEFAULT_LOCATION_ID;
}

/**
 * Como se reparten los gastos comunes entre los locales (Ajustes → Locales):
 *  - `ventas`: en proporcion a las ventas de cada local en el mes del gasto.
 *  - `fijo`: con los porcentajes de `shares` (locationId → %), que suman 100.
 * Sin ajuste, a partes iguales.
 */
export interface SharedCostAllocation {
  method: 'ventas' | 'fijo';
  shares?: Record<string, number>;
}

export interface Supplier {
  tenantId: string;
  supplierId: string;
  name: string;
  contact?: string;
  notes?: string;
}

/* ------------------------------------------------------------------ */
/* Modulo 1: Costeo                                                    */
/* ------------------------------------------------------------------ */

/** Los 12 grupos de la hoja "Ingredientes". */
export type IngredientGroup =
  | 'lacteos_huevos'
  | 'secos_granos'
  | 'abarrotes'
  | 'frutas_verduras'
  | 'carnes_pescados'
  | 'vinos_licores'
  | 'bebidas'
  | 'aceites'
  | 'limpieza'
  | 'desechables'
  | 'subrecetas'
  | 'otros';

export const INGREDIENT_GROUP_LABELS: Record<IngredientGroup, string> = {
  lacteos_huevos: 'Lácteos y huevos',
  secos_granos: 'Secos y granos',
  // "Abarrotes" es mexicano puro; "productos de despensa" se entiende en
  // los dos lados del charco (neutralizacion de terminos, ronda 3).
  abarrotes: 'Productos de despensa',
  frutas_verduras: 'Frutas y verduras',
  carnes_pescados: 'Carnes y pescados',
  vinos_licores: 'Vinos y licores',
  bebidas: 'Bebidas',
  aceites: 'Aceites',
  limpieza: 'Limpieza',
  desechables: 'Desechables',
  subrecetas: 'Subrecetas',
  otros: 'Otros',
};

/** Etiqueta del grupo: los de serie tienen nombre bonito, los personalizados se muestran tal cual. */
export function ingredientGroupLabel(group: string): string {
  return INGREDIENT_GROUP_LABELS[group as IngredientGroup] ?? group;
}

export interface Ingredient {
  tenantId: string;
  ingredientId: string;
  /** Producto */
  name: string;
  /** Marca comercial */
  brand?: string;
  /** Presentacion comercial: garrafón, botella, saco... */
  presentation?: string;
  /** Uno de los 12 grupos de serie o un grupo personalizado del tenant */
  group: IngredientGroup | (string & {});
  supplierId?: string;

  /** Formato de compra: 6 (L), 25 (kg), 500 (pz)... */
  purchaseQty: number;
  purchaseUnit: Unit;

  /**
   * Precio del formato de compra, SIEMPRE sin IVA.
   *
   * Es el unico precio que se guarda (decision de Alex, 1 sep 2026). El
   * precio con IVA se calcula al mostrarlo: si se guardaran los dos como
   * campos independientes, un dia se desincronizan y no hay forma de saber
   * cual de los dos es el bueno.
   */
  netPrice: number;

  /**
   * @deprecated Las tres ofertas de proveedor de la plantilla (Precio 1/2/3).
   * Ya no se escribe: solo se LEE, para los ingredientes dados de alta antes
   * del 1 de septiembre de 2026, que se colapsan a `netPrice` la primera vez
   * que se vuelven a guardar. La comparativa entre precios volvera cuando
   * haya compras y, con ellas, historico de verdad con su fecha.
   */
  prices?: number[];

  /**
   * IVA soportado (0-100). No forma parte del coste, que va siempre neto: se
   * usa para poder teclear el precio con IVA (albaran o tique) y para
   * ensenyar el precio con IVA junto al de compra.
   */
  vatPct: number;

  /** g por mL, solo si se compra en peso y se receta en volumen (o al reves) */
  densityGPerMl?: number;

  active: boolean;
  updatedAt: string;
}

export type ComponentRef =
  | { kind: 'ingredient'; ingredientId: string }
  | { kind: 'recipe'; recipeId: string };

/** Las recetas de Alex tienen dos bloques de lineas. */
export type ComponentBlock = 'ingrediente' | 'empaque';

export interface RecipeComponent {
  ref: ComponentRef;
  block: ComponentBlock;
  /** Cantidad neta: la que acaba en el plato */
  qty: number;
  unit: Unit;
  /**
   * Merma de ESTA linea (0-100). La albahaca merma 35 % en el pesto y 0 %
   * como guarnicion: por eso va en la linea y no en el ingrediente.
   * Cantidad bruta = neta / (1 - merma).
   */
  wastePct: number;
  note?: string;
}

export type RecipeKind = 'plato' | 'subreceta';

/** Los 14 alérgenos de declaración obligatoria en la UE (Reglamento 1169/2011, Anexo II). */
export type Allergen =
  | 'gluten'
  | 'crustaceos'
  | 'huevos'
  | 'pescado'
  | 'cacahuetes'
  | 'soja'
  | 'lacteos'
  | 'frutos_secos'
  | 'apio'
  | 'mostaza'
  | 'sesamo'
  | 'sulfitos'
  | 'altramuces'
  | 'moluscos';

export const ALLERGEN_LABELS: Record<Allergen, string> = {
  gluten: 'Gluten',
  crustaceos: 'Crustáceos',
  huevos: 'Huevos',
  pescado: 'Pescado',
  cacahuetes: 'Cacahuetes',
  soja: 'Soja',
  lacteos: 'Lácteos',
  frutos_secos: 'Frutos secos',
  apio: 'Apio',
  mostaza: 'Mostaza',
  // "Ajonjolí" en Mexico, "sésamo" en España: las dos, como en los grupos.
  sesamo: 'Sésamo (ajonjolí)',
  sulfitos: 'Sulfitos',
  altramuces: 'Altramuces',
  moluscos: 'Moluscos',
};

/** En el orden del Anexo II, que es el que usan las fichas de alérgenos. */
export const ALLERGENS = Object.keys(ALLERGEN_LABELS) as Allergen[];

export interface Recipe {
  tenantId: string;
  recipeId: string;
  name: string;
  kind: RecipeKind;
  /** Codigo interno: PIC001, SUB001 */
  code?: string;
  /** Elaboracion paso a paso, para la ficha de cocina */
  steps?: string;
  /**
   * Foto del plato. Hoy es un data-URL JPEG comprimido en el navegador
   * (~200 KB max); cuando entre el lote de auth real pasara a ser una URL
   * de S3. El limite del schema protege el tope de 400 KB por item de Dynamo.
   */
  photo?: string;
  /** Grupo de carta: Para picar, Principales, Postres... */
  group?: string;
  /** Alérgenos que contiene el plato, para la ficha de cocina y la carta. */
  allergens?: Allergen[];
  components: RecipeComponent[];

  /** Produccion del batch: 2 L de salsa, 12 pz de croquetas */
  yieldQty: number;
  yieldUnit: Unit;
  /** Raciones que salen del batch */
  portions: number;

  /**
   * Margen de seguridad (0-100). Recarga el coste total para cubrir lo que
   * no se registra: cortes, pruebas, errores. Alex usa 10 % por defecto.
   */
  safetyMarginPct: number;

  /**
   * true  -> el coste de referencia es por unidad de rendimiento (subrecetas)
   * false -> el coste de referencia es por racion (platos de carta)
   */
  useYieldCostAsBase: boolean;

  lastCostedAt?: string;
  active: boolean;
  updatedAt: string;
}

/* ------------------------------------------------------------------ */
/* Inventario mensual (recuento de existencias)                        */
/* ------------------------------------------------------------------ */

export interface InventoryCountLine {
  ingredientId: string;
  /** Nombre en el momento del recuento: el historico no cambia si se renombra. */
  name: string;
  /** Cantidad contada, en la unidad de COMPRA del ingrediente (kg, L, pz...). */
  qty: number;
  unit: Unit;
  /**
   * Valor en la moneda del tenant, congelado al guardar con el precio medio
   * vigente. Si el precio cambia despues, el recuento historico no se mueve:
   * las existencias de agosto valen lo que valian en agosto.
   */
  value: number;
}

/**
 * Recuento de existencias a CIERRE de un mes. Es lo que hace posible el
 * consumo real en la cuenta de resultados:
 *
 *   consumo = existencias iniciales + compras del periodo − existencias finales
 *
 * Sin recuentos, la P&L usa las compras a secas (como siempre). La clave
 * natural es tenant + mes (+ centro si se cuenta por centro).
 */
export interface InventoryCount {
  tenantId: string;
  /** YYYY-MM del mes que se cierra con este recuento. */
  month: string;
  /** Centro al que pertenece el recuento; sin el, cuenta para todo el negocio. */
  locationId?: string;
  lines: InventoryCountLine[];
  /** Suma de las lineas, redondeada a 2 decimales. */
  totalValue: number;
  updatedAt: string;
}

export interface MenuItem {
  tenantId: string;
  menuItemId: string;
  recipeId: string;
  name: string;
  /** PVP al publico CON IVA, tal y como aparece en la carta */
  sellingPriceGross: number;
  vatPct: number;
  /** Objetivo de food cost (0-100) para calcular el precio sugerido */
  targetFoodCostPct: number;
  /**
   * Coste teorico de referencia: el coste que tenia el plato la ultima vez
   * que lo revisaste. Se fija SOLO al crear el plato y al pulsar "aceptar
   * coste actual" — nunca se teclea a mano (peticion de Alex: ponerlo plato
   * a plato era una locura). La desviacion contra el coste actual de los
   * ingredientes dispara la alerta de recosteo.
   */
  theoreticalCost?: number;
  /** Cuando se acepto ese coste de referencia (ISO) */
  theoreticalCostAt?: string;
  /** Si se vende por plataforma, su comision sustituye a la bancaria */
  platformId?: string;
  /**
   * PVP por canal (peticion de Alex: "menus por canal"). Clave = platformId.
   * - sin entrada: el canal hereda el PVP de comedor (sellingPriceGross)
   * - numero: PVP propio de ese canal, CON IVA
   * - null: el plato NO se vende en ese canal (fuera de su carta)
   * Solo aplica a platos de carta general (sin platformId): un plato ya
   * clavado a una plataforma tiene su PVP en sellingPriceGross.
   */
  channelPrices?: Record<string, number | null>;
  /**
   * Grupo de carta del PRODUCTO: Entrantes, Principales, Postres, Bebidas…
   *
   * Vive aqui y no en la receta porque es una decision comercial, no de
   * produccion: la receta dice como se hace y cuanto cuesta, el producto dice
   * en que parte de la carta se vende (Alex, 2 sep 2026). Antes solo estaba en
   * la receta, y de ahi salian casos raros como una subreceta con grupo de
   * carta sin llegar a estar nunca en la carta.
   *
   * Vacio = se usa el de la receta, que sigue existiendo como respaldo. Asi
   * los platos que ya estaban dados de alta no pierden su grupo y no hace
   * falta migrar nada: ver `menuGroupOf`.
   */
  group?: string;
  /** Unidades vendidas en el periodo, para menu engineering. Total de todos los locales. */
  unitsSold?: number;
  /**
   * Unidades vendidas por local (7 sep 2026): locationId → unidades. Su suma
   * es `unitsSold`. Un plato guardado antes solo tiene el total, que por
   * convencion es del principal (ver `unitsSoldFor`).
   */
  unitsSoldByLocation?: Record<string, number>;
  active: boolean;
  updatedAt: string;
}

/* ------------------------------------------------------------------ */
/* Panel de administracion de la plataforma (solo Pol y Alex)          */
/* ------------------------------------------------------------------ */

/** Fila del panel de administracion: un restaurante cliente. */
export interface AdminTenantRow {
  tenantId: string;
  name: string;
  /** Cuenta interna de prueba: se lista, pero no cuenta en los totales. */
  internal?: boolean;
  /** Locales extra contratados en Stripe y locales de cortesia regalados desde aqui. */
  extraLocations?: number;
  bonusLocations?: number;
  country: Tenant['country'];
  currency: string;
  plan: PlanId;
  subscriptionStatus: SubscriptionStatus;
  createdAt: string;
  trialEndsAt?: string;
  /**
   * Lo que este cliente aporta al MRR (plan + escaner + asientos + centros).
   * 0 si no tiene suscripcion viva. Ver `mrrDeTenant` en plans.ts.
   */
  mrr: number;
  /** Preguntas al asistente este mes / limite del plan (null = sin limite). */
  assistantUsed: number;
  assistantLimit: number | null;
  /** Coste estimado de la IA de este cliente este mes, en euros. */
  aiCost: number;
  /** Facturas escaneadas este mes. */
  scans: number;
  recipes: number;
  menuItems: number;
  /** Datos de factura: regimen de IVA y que falta por rellenar. Ver tax.ts. */
  billing: EstadoFacturacion;
  /** WhatsApp del propietario, si lo dejo (los Founder lo dan en el alta). */
  whatsapp?: string;
  /**
   * Cobros de Stripe que NO han acabado en una factura de Holded: el contacto
   * sin la serie asignada, el IVA que no cuadra, la API caida... Cada uno es
   * un cobro real sin factura, y alguien tiene que mirarlo.
   */
  /** Se dio de alta con invitación Founder y aún no ha pagado: solo ve la página de pago. */
  founderPending?: boolean;
  holdedPendientes?: number;
  /** Por qué está pendiente la primera (p. ej. el contacto sin serie en Holded). */
  holdedMotivo?: string;
  /** Números de las facturas ya emitidas en Holded (AR26/0002…), la más reciente primero. */
  holdedNumeros?: string[];
}

export interface AdminOverview {
  generatedAt: string;
  month: string;
  totals: {
    /** Clientes reales (sin las cuentas internas). */
    tenants: number;
    /** Cuentas internas de prueba excluidas de todos los totales. */
    internos: number;
    byPlan: Record<PlanId, number>;
    byStatus: Record<SubscriptionStatus, number>;
    /**
     * MRR: suma de lo que paga cada suscripcion viva (activa o con impago
     * pendiente), incluyendo modulos, asientos y centros extra. Es una
     * estimacion desde nuestros datos: la cifra exacta la da Stripe.
     */
    mrrEstimate: number;
    /** MRR guardado en la foto del mes anterior. null = aun no hay historia. */
    mrrPrevMonth: number | null;
    /** Crecimiento del MRR contra el mes anterior, en %. null = sin historia. */
    mrrGrowthPct: number | null;
    /** ARR = MRR x 12. */
    arr: number;
    /** Clientes que pagan (activos + impagados). */
    paying: number;
    /** Ingreso medio por cliente de pago (MRR / paying). */
    arpu: number;
    /** Altas de este mes y del anterior, para ver el ritmo. */
    newThisMonth: number;
    newPrevMonth: number;
    /**
     * De los clientes que ya han salido de la prueba, % que paga.
     * paying / (paying + canceled). null si aun no ha salido nadie.
     */
    conversionPct: number | null;
    /** Coste estimado de la IA de todos los clientes este mes, en euros. */
    aiCostThisMonth: number;
    /** Facturas escaneadas entre todos los clientes este mes. */
    scansThisMonth: number;
    /** (MRR - coste IA) / MRR, en %. null si el MRR es 0. */
    grossMarginPct: number | null;
    /** Pruebas que caducan en los proximos 7 dias. */
    trialsEndingSoon: number;
    /** Clientes de pago a los que todavia NO se les puede emitir factura. */
    sinDatosFiscales: number;
    assistantQuestionsThisMonth: number;
  };
  tenants: AdminTenantRow[];
}

/* ------------------------------------------------------------------ */
/* P&L de la propia plataforma (los numeros de Pol y Alex)             */
/* ------------------------------------------------------------------ */

/**
 * Categorias de gasto del negocio de Alexia Rest. Pocas a proposito: esto es
 * la contabilidad interna de dos socios, no un plan de cuentas.
 * El coste de AWS NO se apunta aqui: entra solo desde Cost Explorer.
 */
export type PlatformExpenseCategory =
  | 'software'
  | 'marketing'
  | 'gestoria'
  | 'bancario'
  | 'otros';

export const PLATFORM_EXPENSE_CATEGORY_LABELS: Record<PlatformExpenseCategory, string> = {
  software: 'Software y herramientas',
  marketing: 'Marketing y captación',
  gestoria: 'Gestoría y legal',
  bancario: 'Comisiones bancarias y Stripe',
  otros: 'Otros gastos',
};

export const PLATFORM_EXPENSE_CATEGORIES = Object.keys(
  PLATFORM_EXPENSE_CATEGORY_LABELS,
) as PlatformExpenseCategory[];

export interface PlatformExpenseInput {
  /** YYYY-MM al que se imputa el gasto. */
  month: string;
  category: PlatformExpenseCategory;
  concept: string;
  /** Importe en euros, sin IVA (la P&L es de gestion, no fiscal). */
  amount: number;
}

export interface PlatformExpense extends PlatformExpenseInput {
  expenseId: string;
  /** Email del admin que lo apunto. */
  createdBy?: string;
  createdAt: string;
}

/**
 * Gasto FIJO del negocio que se repite cada mes (dominio, Google Workspace,
 * gestoria...): se apunta una vez con el mes en que empieza y, si acaba, el
 * mes en que acaba, y la P&L lo imputa a cada mes del tramo.
 */
export type PlatformRecurringPeriod = 'mensual' | 'anual';

export interface PlatformRecurringExpenseInput {
  category: PlatformExpenseCategory;
  concept: string;
  /** Importe en euros, sin IVA, por periodo (al mes o al anyo). */
  amount: number;
  /** Cada cuanto se paga. Un coste anual (el dominio) se prorratea a doceavos. Ausente = mensual. */
  period?: PlatformRecurringPeriod;
  /** Primer mes al que se imputa (YYYY-MM). */
  startMonth: string;
  /** Ultimo mes al que se imputa (YYYY-MM), inclusive. Vacio = sigue vigente. */
  endMonth?: string;
}

export interface PlatformRecurringExpense extends PlatformRecurringExpenseInput {
  recurringId: string;
  createdBy?: string;
  createdAt: string;
}

/**
 * Cierre del reparto de un mes: si la factura de Alex ya esta hecha y pagada.
 * Se guardan las cifras del momento del cierre, para que un gasto apuntado
 * mas tarde no cambie lo que ya se repartio.
 */
export interface PlatformSettlement {
  month: string;
  done: boolean;
  /** Cuando se marco como hecho (ISO). */
  settledAt?: string;
  /** Beneficio del mes en el momento del cierre. */
  resultado: number;
  /** Base de la factura de Alex en el momento del cierre. */
  alexBase: number;
  /** Lo que le quedo a Pol despues de Sociedades, en el momento del cierre. */
  polNeto: number;
  taxRate: number;
  note?: string;
  updatedBy?: string;
  updatedAt: string;
}

/** Ajustes del reparto (los guarda Administracion). */
export interface PlatformSettings {
  /** Tipo de Impuesto de Sociedades (0-100). */
  taxRate: number;
  /**
   * Tope mensual de gasto en IA de TODA la plataforma, en euros. Al llegar,
   * Alexia deja de contestar hasta el mes siguiente. Es el freno de mano del
   * unico coste variable del negocio (Pol, 5 sep 2026: nunca pasar de 5.000).
   */
  aiMonthlyCapEur: number;
}

/** Un mes de la P&L de la plataforma. Todo en euros. */
export interface PlatformPnlMonth {
  month: string;
  /** Ingresos del mes y de donde salen. */
  ingresos: number;
  /**
   * stripe    -> facturas cobradas de verdad (sin IVA)
   * estimado  -> MRR estimado (Stripe no configurado o mes sin facturas registradas)
   * sin_datos -> no hay ni Stripe ni foto del MRR de ese mes
   */
  ingresosSource: 'stripe' | 'estimado' | 'sin_datos';
  /** Factura de AWS del mes convertida a euros. null = no disponible aun. */
  awsCost: number | null;
  /** La misma factura en USD, tal cual la da Cost Explorer. */
  awsCostUsd: number | null;
  /**
   * Coste estimado de la IA (Bedrock). Va APARTE de la factura de AWS: el
   * consumo de modelos no lleva la etiqueta del proyecto y Cost Explorer no
   * lo cuenta en `awsCost`.
   */
  aiCost: number;
  /** Comisión real de Stripe sobre los cobros del mes (de sus movimientos de saldo). */
  stripeFees: number;
  /** Gastos apuntados a mano este mes. */
  manualExpenses: PlatformExpense[];
  manualTotal: number;
  /** Gastos fijos recurrentes que tocan este mes. */
  fixedExpenses: PlatformRecurringExpense[];
  fixedTotal: number;
  /** AWS (si se conoce) + IA + fijos + gastos manuales. */
  totalGastos: number;
  resultado: number;
  /** Lo que toca a cada socio antes de impuestos (50 %). */
  porSocio: number;
  /** Reparto con Sociedades: factura de Alex y neto de Pol. */
  reparto: PlatformReparto;
  /** Cierre del reparto, si se ha marcado. */
  settlement: PlatformSettlement | null;
  /** true = el mes ya ha terminado y se puede repartir. */
  cerrable: boolean;
}

export interface PlatformPnlResponse {
  year: number;
  generatedAt: string;
  currency: 'EUR';
  /** Tipo de cambio aplicado a la factura de AWS (EUR por 1 USD). */
  usdToEur: number;
  /** Reparto societario: 0.5 = socios al 50 %. */
  partnerShare: number;
  /** Tipo de Sociedades aplicado al reparto. */
  taxRate: number;
  /** Tope mensual de gasto en IA de la plataforma, en euros. */
  aiMonthlyCapEur: number;
  /** Gastos fijos dados de alta (vigentes o no). */
  recurring: PlatformRecurringExpense[];
  months: PlatformPnlMonth[];
  totals: {
    ingresos: number;
    gastos: number;
    resultado: number;
    porSocio: number;
    /** Suma de las bases de las facturas de Alex del anyo. */
    alexBase: number;
    polNeto: number;
    impuestoSociedades: number;
    /** De la parte de Alex, lo ya repartido (meses cerrados) y lo pendiente. */
    alexRepartido: number;
    alexPendiente: number;
  };
}

/* ------------------------------------------------------------------ */
/* Modulo 2: Financiero                                                */
/* ------------------------------------------------------------------ */

/**
 * Metodos de pago de la hoja "Ingresos <Mes>", bloque "En el local".
 *
 * Decision de Alex (feedback 3): credito y debito se juntan en **tarjeta** —
 * los TPV rara vez lo separan y para el dueño da igual. Los datos antiguos con
 * `debito`/`credito` separados se funden en `tarjeta` al leerlos
 * (`foldInHouse`), sin migracion destructiva.
 */
export type PaymentMethod = 'efectivo' | 'tarjeta' | 'otros';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  efectivo: 'Efectivo',
  tarjeta: 'Tarjeta',
  otros: 'Otros métodos de pago',
};

/** Orden en que se muestran los métodos en la rejilla y la P&L. */
export const PAYMENT_METHODS: PaymentMethod[] = ['efectivo', 'tarjeta', 'otros'];

/**
 * Funde el desglose por método de un día, colapsando los métodos antiguos
 * (`debito` + `credito`) en `tarjeta`. Idempotente y tolerante a claves que ya
 * no existen. Es el único punto que conoce la unificación de tarjeta.
 */
export function foldInHouse(
  inHouse: Record<string, number> | undefined | null,
): Record<PaymentMethod, number> {
  const src = inHouse ?? {};
  return {
    efectivo: src.efectivo ?? 0,
    tarjeta: (src.tarjeta ?? 0) + (src.debito ?? 0) + (src.credito ?? 0),
    otros: src.otros ?? 0,
  };
}

/**
 * Un dia de facturacion. En la plantilla es una columna de la rejilla de
 * ingresos; aqui es un registro, para poder tener historico sin limite de anyo.
 * Todos los importes van SIN IVA.
 */
export interface DailyRevenue {
  tenantId: string;
  locationId?: string;
  date: string; // YYYY-MM-DD
  /** Ventas en el local por metodo de cobro */
  inHouse: Record<PaymentMethod, number>;
  /** Ventas a domicilio por plataforma (clave = platformId) */
  delivery: Record<string, number>;
  /*
   * No hay desglose por tipo de producto (alimentos/bebidas...): la unica
   * clasificacion de los ingresos es metodo de pago + plataforma. Ver la nota
   * en TenantSettings. Un dia guardado con `byProductType` se lee igual y la
   * clave se ignora.
   */
  /** Comensales atendidos */
  customers?: number;
  /** Mesas servidas */
  tables?: number;
  /** Invitaciones: no facturan pero consumen materia prima */
  courtesies?: number;
  /** Descuentos aplicados sobre la venta */
  discounts?: number;
  notes?: string;
  /**
   * De donde salio el dia: ausente = tecleado o importado a mano;
   * `pos:loyverse` / `pos:square` = lo escribio la sincronizacion del TPV.
   * Una re-sincronizacion sobreescribe los importes pero respeta lo que el
   * usuario haya tecleado aparte (comensales, mesas, notas).
   */
  source?: string;
  updatedAt: string;
}

/** Plan de cuentas literal de Alex (hoja oculta Tabs, C1:C22). */
export type ExpenseCategory =
  | 'compras_materia_prima'
  | 'otros_insumos'
  | 'sueldos_salarios'
  | 'cuotas_ss'
  | 'alquiler'
  | 'reparaciones'
  | 'honorarios'
  | 'seguros'
  | 'comisiones_bancarias'
  | 'uniformidad'
  | 'publicidad_rrss'
  | 'comunicacion_web'
  | 'suministros_agua'
  | 'suministros_electricidad'
  | 'suministros_gas'
  | 'servicios_varios'
  | 'telefonia'
  | 'papeleria'
  | 'otros_gastos'
  | 'amortizaciones'
  | 'intereses'
  | 'impuesto_ganancias';

export type CostNature = 'fijo' | 'variable';

/** Contado = pagado al momento. Credito = factura con vencimiento. */
export type PaymentType = 'contado' | 'credito';

/** Estado de la factura del proveedor. */
export type InvoiceStatus = 'facturado' | 'pendiente' | 'sin_factura';

/** Estado de una deuda a credito. Se deriva, no se guarda. */
export type PayableStatus = 'pagada' | 'por_vencer' | 'vencida';

export interface ExpenseEntry {
  tenantId: string;
  entryId: string;
  locationId?: string;
  paymentType: PaymentType;

  /** Contado: fecha del gasto. Credito: fecha de emision de la factura. */
  date: string;
  /** Solo credito: cuando vence el pago */
  dueDate?: string;
  /** Cuando salio realmente el dinero (para el flujo de caja) */
  paymentDate?: string;

  concept: string;
  supplierId?: string;
  invoiceNumber?: string;

  category: ExpenseCategory;
  /**
   * Clave en S3 de la factura escaneada, si el gasto vino de una foto. Es el
   * justificante: se guarda para poder ensenarlo y para la gestoria.
   */
  attachmentKey?: string;
  /** Subgrupo propio del restaurante (Carnes, Sala...), dentro del grupo. */
  subgroup?: string;
  nature: CostNature;
  /** Importe SIN IVA */
  netAmount: number;
  vatPct: number;

  paid: boolean;
  invoiceStatus: InvoiceStatus;
  notes?: string;
  /**
   * Solo en lecturas por local: un gasto comun del grupo entra en la cuenta
   * de este local con este % de su importe (ver `allocateSharedExpenses`).
   * Nunca se guarda.
   */
  allocatedPct?: number;
}

export interface Investment {
  tenantId: string;
  investmentId: string;
  /** Centro al que pertenece. Ausente = principal (ver Location). */
  locationId?: string;
  /** Fecha de adquisicion */
  date: string;
  concept: string;
  /** Unidades adquiridas */
  quantity: number;
  /** Precio unitario SIN IVA */
  unitPrice: number;
  vatPct: number;
  /** Años de vida util. 0 = se imputa integro como gasto. */
  usefulLifeYears: number;
  fundingSource?: 'propio' | 'prestamo' | 'leasing' | 'subvencion';
  notes?: string;
}

/** Movimientos de caja que no son ni venta ni gasto de explotacion. */
export type CashMovementKind =
  | 'ingresos_inversiones'
  | 'financiacion_aportes'
  | 'otros_ingresos'
  | 'pago_impuestos'
  | 'otros_gastos'
  | 'ingresos_financieros'
  | 'gastos_financieros';

export interface CashMovement {
  tenantId: string;
  movementId: string;
  /** Centro al que pertenece. Ausente = principal (ver Location). */
  locationId?: string;
  date: string;
  kind: CashMovementKind;
  concept: string;
  /** Importe con signo positivo; el tipo determina si suma o resta */
  amount: number;
  notes?: string;
}

/**
 * Objetivo mensual contra el que se compara el real (hoja KPIs).
 *
 * Va por centro (7 sep 2026): un grupo fija objetivos distintos para cada
 * local. `locationId` ausente = centro principal, como en el resto de
 * registros operativos. El consolidado no se guarda: se compone sumando los
 * objetivos de los centros (`consolidateTargets`).
 */
export interface Target {
  tenantId: string;
  locationId?: string;
  month: string; // YYYY-MM
  netRevenue?: number;
  /** Ticket promedio por COMENSAL */
  averageTicket?: number;
  /**
   * Ticket promedio por MESA (u orden en delivery). Alex lo pidio el 12 de
   * agosto: un menu del dia sube el ticket por mesa sin mover el de comensal.
   */
  averageTicketPerTable?: number;
  materialPurchases?: number;
  foodCostPct?: number;
  laborCostPct?: number;
  /** Objetivo conjunto de materia prima + personal. */
  primeCostPct?: number;
  /** Objetivo de rentabilidad: EBITDA sobre ventas (%). */
  ebitdaPct?: number;
  /** Caída de ventas que el negocio quiere poder soportar antes del equilibrio. */
  safetyMarginPct?: number;
}

/* ------------------------------------------------------------------ */
/* Resultados calculados                                               */
/* ------------------------------------------------------------------ */

export interface IngredientCost {
  ingredientId: string;
  name: string;
  /** Nombre completo tal y como aparece en las recetas de Alex */
  displayName: string;
  /** Precio del formato de compra, sin IVA. Es el dato guardado. */
  netPrice: number;
  /** El mismo precio con el IVA soportado aplicado. Es un calculo, no un dato. */
  grossPrice: number;
  /** €/unidad base (g, mL o pz). Es el numero de CALCULO. */
  costPerBaseUnit: number;
  baseUnit: Unit;
  /**
   * €/unidad de compra (€/kg, €/L, €/lb...). Es el numero que se ENSEÑA:
   * quien compra un saco de 25 kg piensa en euros por kilo, no por gramo.
   */
  costPerPurchaseUnit: number;
  purchaseUnit: Unit;
}

export interface RecipeCostLine {
  ref: ComponentRef;
  block: ComponentBlock;
  name: string;
  qty: number;
  unit: Unit;
  wastePct: number;
  /** Cantidad que hay que comprar: neta / (1 - merma) */
  grossQty: number;
  unitCost: number;
  /**
   * Unidad a la que se refiere `unitCost`. NO tiene por que coincidir con
   * `unit`: un aceite comprado en litros y recetado en gramos se cuesta por
   * mL. Sin este campo la columna "coste unitario" era un numero suelto que
   * no se podia interpretar.
   */
  unitCostUnit: Unit;
  cost: number;
  /** % sobre el coste total de la receta */
  weightPct: number;
}

export interface RecipeCost {
  recipeId: string;
  name: string;
  lines: RecipeCostLine[];
  /** Coste de las lineas del bloque de ingredientes */
  ingredientCost: number;
  /** Coste de las lineas del bloque de empaque */
  packagingCost: number;
  /** Suma de ambos bloques, antes del margen de seguridad */
  subtotal: number;
  safetyMarginPct: number;
  safetyMarginAmount: number;
  /** subtotal + margen de seguridad */
  totalBatchCost: number;
  portions: number;
  costPerPortion: number;
  /** Coste por unidad de rendimiento, para encadenar subrecetas */
  costPerYieldUnit: number;
  yieldQty: number;
  yieldUnit: Unit;
  /** El que se usa como referencia segun useYieldCostAsBase */
  baseCost: number;
  warnings: string[];
}

export type PricingStatus = 'ok' | 'ajustado' | 'infraprecio';

export interface PricingAnalysis {
  menuItemId: string;
  name: string;
  costPerPortion: number;
  sellingPriceGross: number;
  sellingPriceNet: number;
  vatPct: number;

  /** Comision del datafono (0 si se vende por plataforma) */
  bankCommission: number;
  platformId?: string;
  platformName?: string;
  platformCommission: number;

  /** coste / (precio neto - comision bancaria) */
  foodCostPct: number;
  /** precio neto - comisiones - coste */
  grossMargin: number;
  grossMarginPct: number;
  markupFactor: number;

  theoreticalCost?: number;
  /** 1 - teorico/actual, en %. Positivo = el coste ha subido. */
  costDeviationPct?: number;

  suggestedPriceNet: number;
  suggestedPriceGross: number;
  priceGapGross: number;

  status: PricingStatus;
  alerts: string[];
}

export interface ProfitAndLoss {
  periodStart: string;
  periodEnd: string;
  revenue: number;
  courtesies: number;
  discounts: number;
  netRevenue: number;
  materialCost: number;
  laborCost: number;
  primeCost: number;
  primeCostPct: number;
  grossProfit: number;
  grossMarginPct: number;
  operatingExpenses: number;
  ebitda: number;
  ebitdaPct: number;
  depreciation: number;
  ebit: number;
  financialExpenses: number;
  ebt: number;
  taxes: number;
  netProfit: number;
  netProfitPct: number;
  byCategory: Record<ExpenseCategory, number>;
  /**
   * Desglose de los ingresos según lo que el usuario registró (petición de
   * Alex: "los ingresos no se desglosan según lo que elegiste"). Por método de
   * cobro (clave = método) y por plataforma de domicilio (clave = platformId).
   * La cuenta de resultados los enseña al desplegar la línea de Ingresos.
   */
  revenueByMethod: Record<string, number>;
  revenueByPlatform: Record<string, number>;
  /**
   * Consumo real de materia prima cuando hay recuentos de inventario:
   *
   *   materialConsumption = materialCost (compras) + opening − closing
   *
   * Con `hasInventory` la CASCADA entera (margen bruto, prime cost, EBITDA...)
   * usa el consumo en vez de las compras; `materialCost` conserva su
   * significado de siempre (solo compras) para poder desglosarlo.
   */
  hasInventory: boolean;
  openingInventory: number;
  closingInventory: number;
  materialConsumption: number;
  /**
   * Desglose de gastos por subgrupo (Carnes, Bodega, Cocina…), para poder
   * desplegar cada grupo de gasto por sus subgrupos además de por categoría.
   * `group` es 'materia_prima' | 'personal' | 'generales'.
   */
  subgroups: Array<{ group: string; subgroup?: string; total: number; count: number }>;
}

export interface CashFlowMonth {
  month: string; // YYYY-MM
  salesBank: number;
  salesCash: number;
  investmentIncome: number;
  funding: number;
  otherIncome: number;
  totalInflow: number;

  operatingPayments: number;
  taxPayments: number;
  otherPayments: number;
  totalOutflow: number;

  /** Flujo de caja economico = cobros - pagos */
  economicFlow: number;
  financialIncome: number;
  financialExpenses: number;
  financialFlow: number;

  openingBalance: number;
  /** Flujo de caja financiero acumulado */
  closingBalance: number;

  /** IVA repercutido - soportado del mes: dinero que no es tuyo */
  vatBalance: number;
  balanceExcludingVat: number;
}

export interface Payable {
  entryId: string;
  concept: string;
  supplierId?: string;
  invoiceNumber?: string;
  dueDate: string;
  grossAmount: number;
  status: PayableStatus;
  daysOverdue: number;
}

export interface Kpis {
  periodStart: string;
  periodEnd: string;
  revenue: number;
  netRevenue: number;
  foodCostPct: number;
  laborCostPct: number;
  primeCostPct: number;
  ebitdaPct: number;
  customers: number;
  tables: number;
  /** Facturacion / comensales */
  averageTicketPerCustomer: number;
  /** Facturacion / mesas */
  averageTicketPerTable: number;
  breakEvenRevenue: number;
  contributionMarginPct: number;
  safetyMarginPct: number;
  fixedCosts: number;
  variableCosts: number;
  fixedCostPct: number;
  variableCostPct: number;
  /** Compras de materia prima del periodo en importe (no en %): el objetivo
   * "Compras de materia prima" se compara contra esto. */
  materialCost: number;
}

export interface WeekdayPerformance {
  weekday: number; // 1 = lunes ... 7 = domingo
  label: string;
  revenue: number;
  customers: number;
  averageTicket: number;
  days: number;
}

/**
 * `vaca` se conserva como identificador interno (hay datos y codigo que lo
 * usan); de cara al usuario se muestra "Caballo de batalla" (ronda 3).
 */
export type MenuEngineeringClass = 'estrella' | 'vaca' | 'puzzle' | 'perro';

export interface MenuEngineeringItem {
  menuItemId: string;
  name: string;
  /**
   * Grupo de carta del plato (de su receta). La clasificacion se calcula
   * DENTRO del grupo: comparar la popularidad de un postre con la de un
   * principal no dice nada (ronda 3). null = plato sin grupo asignado.
   */
  group: string | null;
  unitsSold: number;
  /** % de unidades vendidas sobre el total de SU grupo en el periodo. */
  popularityPct: number;
  contributionMargin: number;
  totalContribution: number;
  /** Limites usados para clasificar este plato (los de su grupo). Alexia los
   * necesita para explicar por que un plato cae en un cuadrante. */
  popularityThresholdPct: number;
  marginThreshold: number;
  classification: MenuEngineeringClass;
  recommendation: string;
}

export interface TargetComparison {
  month: string;
  metric: string;
  label: string;
  target?: number;
  actual: number;
  deviation?: number;
  deviationPct?: number;
  achieved?: boolean;
}


/**
 * Una factura de la suscripcion, tal como la ve el restaurante. Viene de
 * Holded, que es donde se emiten: asi la lista incluye tambien las hechas a
 * mano y nunca esta desfasada respecto a lo que tiene la gestoria.
 */
export interface Invoice {
  id: string;
  /** El numero de verdad, p. ej. AR26/0002. Los borradores no se listan. */
  numero: string;
  /** YYYY-MM-DD */
  fecha?: string;
  totalCents: number;
  baseCents: number;
}
